import { EventType, type IUserEngagementEvent, publishEvent } from "@/events";
import { COOKIE_KEYS } from "@/events/constants";
import { type ClassValue, clsx } from "clsx";
import type { TFunction } from "i18next";
import { twMerge } from "tailwind-merge";

export interface FAQData {
  id: string;
  question: string;
  answer: string;
}

export const getFAQsFromTranslations = (t: TFunction): FAQData[] => {
  const faqs: FAQData[] = [];
  const questions = t("faq.questions", "", { returnObjects: true }) as Record<
    string,
    { question: string; answer: string }
  >;

  for (const [key, value] of Object.entries(questions)) {
    if (value.question && value.answer) {
      faqs.push({
        id: key,
        question: value.question,
        answer: value.answer,
      });
    }
  }
  return faqs;
};

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const getCookieValue = (key: string) => {
  const cookies = document.cookie.split(";").map((cookie) => cookie.trim());
  for (const cookie of cookies) {
    const [cookieKey, cookieValue] = cookie.split("=");
    if (cookieKey === key) {
      return cookieValue;
    }
  }
  return "";
};

export const isKpMetaScriptPresent = () => {
  const merchantConfigurations = getCookieValue(COOKIE_KEYS.KP_MERCHANT_CONFIG)
    ? JSON.parse(getCookieValue(COOKIE_KEYS.KP_MERCHANT_CONFIG))
    : null;

  if (
    merchantConfigurations?.meta_configurations &&
    window?.__kwikpass?.firePixelEventViaKP
  ) {
    return true;
  }

  const metaConfigurationThirdPartyServiceProviders =
    merchantConfigurations?.thirdPartyServiceProviders?.find(
      (serviceProvider) => serviceProvider?.name === "meta_s2s",
    );

  if (
    metaConfigurationThirdPartyServiceProviders &&
    window?.__kwikpass?.firePixelEventViaKP
  ) {
    return true;
  }

  return false;
};

export function debounce<F extends (...args: Parameters<F>) => ReturnType<F>>(
  func: F,
  wait: number,
): (...args: Parameters<F>) => void {
  let timeoutId: NodeJS.Timeout | null = null;

  return function (this: unknown, ...args: Parameters<F>): void {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    timeoutId = setTimeout(() => {
      func.apply(this, args);
      timeoutId = null;
    }, wait);
  };
}

export const fireUserEngagementEvent = () => {
  const event: IUserEngagementEvent = {
    type: EventType.USER_ENGAGEMENT,
  };
  publishEvent(event);
};

/**
 * Converts a paisa amount to rupees with two decimal precision.
 * @param paisa - The amount in paisa (e.g., 1299)
 * @returns The amount in rupees as a number (e.g., 12.99)
 */
export const convertPaisaToRupees = (paisa: number): number => {
  return +(paisa / 100).toFixed(2);
};

export const stripHtml = (html: string): string => {
  if (!html) return "";
  return html.replace(/<[^>]*>?/gm, "").trim();
};

export const formatShopifyVariantId = (variantId: string): string => {
  if (!variantId.startsWith("gid://shopify/ProductVariant/")) {
    return `gid://shopify/ProductVariant/${variantId}`;
  }
  return variantId;
};
